import { useMemo } from 'react'
import { isClient } from 'utils/environment'
import useConfig from 'modules/config/state/use-config'
import { Config } from 'modules/config/state/config-types'
import { OrderCustomAnswersSlice } from '../custom-answers/custom-answers-types'
import { OrderContactAnswersSlice } from '../contact-answers/contact-answers-types'

interface UseOrderStorageReturn {
  metadata: {
    get: () => Record<string, unknown> | undefined
  }

  customAnswers: {
    get: () => OrderCustomAnswersSlice
    set: (data: OrderCustomAnswersSlice) => void
  }

  contactAnswers: {
    get: () => Record<string, unknown>
    set: (data: OrderContactAnswersSlice) => void
  }
}

const useOrderStorage = (): UseOrderStorageReturn => {
  const config = useConfig()

  // Session storage keys
  const customQuestionsKey = `custom_questions_${config.campaignId}`

  const customAnswers = useMemo(
    () => ({
      get: (): OrderCustomAnswersSlice => {
        return getCustomAnswersFromStorage(config)
      },

      set: (data: OrderCustomAnswersSlice): void => {
        const existingAnswers = getSessionStorageItem(customQuestionsKey)
        const updatedAnswers = { ...existingAnswers, ...data }
        setSessionStorageItem(customQuestionsKey, updatedAnswers)
      },
    }),
    [config, customQuestionsKey],
  )

  const contactAnswers = useMemo(
    () => ({
      get: (): Record<string, unknown> => {
        return getContactAnswersFromStorage(config)
      },

      set: (data: OrderContactAnswersSlice): void => {
        const existingAnswers = getSessionStorageItem(customQuestionsKey)
        const updatedAnswers = { ...existingAnswers, ...data }
        setSessionStorageItem(customQuestionsKey, updatedAnswers)
      },
    }),
    [config, customQuestionsKey],
  )

  const metadata = useMemo(
    () => ({
      get: (): Record<string, unknown> | undefined => {
        return getMetadataFromStorage(config)
      },
    }),
    [config],
  )

  return {
    metadata,
    customAnswers,
    contactAnswers,
  }
}

export default useOrderStorage

// Utility function to get custom answers from session storage
export const getCustomAnswersFromStorage = (config: Config): OrderCustomAnswersSlice => {
  const customQuestionsKey = `custom_questions_${config.campaignId}`
  const allAnswers = getSessionStorageItem(customQuestionsKey)
  const customAnswers: OrderCustomAnswersSlice = {}

  // Get custom question IDs from config
  const customQuestionIds = new Set(config.questions.custom.map((q) => q.id.toString()))

  // Filter answers that belong to custom questions
  Object.entries(allAnswers).forEach(([answerId, value]) => {
    if (customQuestionIds.has(answerId)) {
      customAnswers[answerId] = value as string | number | boolean | undefined
    }
  })

  return customAnswers
}

// Utility function to get contact answers from session storage
export const getContactAnswersFromStorage = (config: Config): Record<string, unknown> => {
  const customQuestionsKey = `custom_questions_${config.campaignId}`
  const allAnswers = getSessionStorageItem(customQuestionsKey)
  const contactAnswers: Record<string, unknown> = {}

  // Get contact question IDs from config (questions with tags)
  const contactQuestionIds = new Set(
    config.questions.contact.map((q) => ('id' in q ? q.id?.toString() : undefined)).filter(Boolean),
  )

  // Filter answers that belong to contact questions
  Object.entries(allAnswers).forEach(([answerId, value]) => {
    if (contactQuestionIds.has(answerId)) {
      contactAnswers[answerId] = value
    }
  })

  return contactAnswers
}

// Utility function to get metadata from session storage
export const getMetadataFromStorage = (config: Config): Record<string, unknown> | undefined => {
  const metadataKey = `metadata_${config.campaignId}`
  const metadata = getSessionStorageItem(metadataKey)

  return metadata &&
    typeof metadata === 'object' &&
    !Array.isArray(metadata) &&
    Object.keys(metadata).length > 0
    ? metadata
    : undefined
}

// Helper function to safely access session storage
const getSessionStorageItem = (key: string): Record<string, unknown> => {
  if (!hasSessionStorage()) return {}

  try {
    const item = window.sessionStorage.getItem(key)

    return item ? JSON.parse(item) : {}
  } catch (error) {
    console.error(`Error reading from session storage key "${key}":`, error)

    return {}
  }
}

// Helper function to safely set session storage
const setSessionStorageItem = (key: string, value: Record<string, unknown>): void => {
  if (!hasSessionStorage()) return

  try {
    window.sessionStorage.setItem(key, JSON.stringify(value))
  } catch (error) {
    console.error(`Error writing to session storage key "${key}":`, error)
  }
}

// Helper function to check if session storage is available
const hasSessionStorage = (): boolean => {
  return isClient && typeof window !== 'undefined' && !!window.sessionStorage
}
